import { useState } from "react";
import { Link } from "react-router";
import { useOfferData, useOfferMutations } from "../hooks/useOfferData";
import { deleteOffer, setOfferPinned, setOfferUsed, type OfferRow } from "../lib/offersPage";
import { offerView } from "../lib/offerView";
import { TrackedOfferList } from "./TrackedOfferList";

/** One mutation path for both the card sheet and the Offers page. */
export function OfferRows({ offers, onError }: { offers: OfferRow[]; onError: (message: string) => void }) {
  const { isBusy, run } = useOfferMutations();
  const act = (ids: number[], operation: () => Promise<unknown>) => {
    if (ids.some(isBusy)) return;
    onError("");
    void run(ids, operation).catch((error: unknown) => {
      onError(error instanceof Error ? error.message : String(error));
    });
  };
  return <TrackedOfferList offers={offers} isBusy={isBusy}
    onToggleUsed={row => act([row.id], () => setOfferUsed(row.id, row.action === "mark_used"))}
    onDelete={row => act([row.id], () => deleteOffer(row.id))}
    onTogglePinned={rows => act(rows.map(row => row.id), () => rows.reduce<Promise<unknown>>(
      (previous, row) => previous.then(() => setOfferPinned(row.id, !rows.some(value => value.pinned))), Promise.resolve()))} />;
}

export function CardOffers({ cardId }: { cardId: number }) {
  const { data, error } = useOfferData();
  const [actionError, setActionError] = useState<string | null>(null);
  const rows = data?.offers.filter(row => row.card_id === cardId) ?? [];
  return <section aria-label="Card offers" className="space-y-3">
    <p className="text-xs text-neutral-400">The same offers and actions as your portfolio. Activation is confirmed by an issuer read, not by queuing it.</p>
    {(error || actionError) && <p role="alert" className="text-sm text-red-300">{error || actionError}</p>}
    {!data && !error && <p className="text-sm text-neutral-400">Loading offers…</p>}
    {data && rows.length === 0 && <p className="text-sm text-neutral-400">No tracked offers for this card.</p>}
    {(["ready", "confirmation", "activate", "history"] as const).map(view => {
      const selected = rows.filter(row => offerView(row) === view);
      if (!selected.length) return null;
      const label = { ready: "Ready to use", confirmation: "Needs confirmation", activate: "To activate", history: "History" }[view];
      return <section key={view} aria-label={label}>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-400">{label}</h3>
        <div className="overflow-hidden rounded-xl border border-neutral-800"><OfferRows offers={selected} onError={setActionError} /></div>
      </section>;
    })}
    <Link to={`/offers?card=${cardId}`} className="btn btn-ghost btn-sm">View all offers for this card</Link>
  </section>;
}
